import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "../auth";
import RequirePermission from "../components/RequirePermission";
import { Alert, Loading } from "../components/ui";
import * as P from "../constants/permissions";
import {
  OrganizationSettings,
  AccountSettings,
} from "./settings/OrganizationAccount";
import TeamSettings from "./settings/TeamSettings";
import BillingSettings from "./settings/BillingSettings";
import IntegrationSettings from "./settings/IntegrationSettings";
import PrinterSettings from "./settings/PrinterSettings";
import "./settings/shared";
import "./settings/settings.css";

export default function Settings() {
  const { t } = useTranslation("settings");
  const [params, setParams] = useSearchParams();
  const {
    permissions,
    organizationSetupRequired,
    isCurrentUserLoading,
    currentUser,
    impersonatedOrgId,
  } = useCurrentUser();
  const can = (p: string) => permissions.includes(p);
  const sections = [
    { key: "organization", permission: P.ORGANIZATION_EDIT_PERMISSION },
    { key: "team", permission: P.ORGANIZATION_LIST_USERS_PERMISSION },
    { key: "billing", permission: "" },
    { key: "integrations", permission: P.ORGANIZATION_EDIT_PERMISSION },
    { key: "printers", permission: P.PRINTER_LIST_PERMISSION },
    { key: "account", permission: "" },
  ];
  const requested = organizationSetupRequired
    ? "organization"
    : params.get("section") || "account";
  const selected =
    sections.find((section) => section.key === requested) || sections[5];
  if (isCurrentUserLoading || !currentUser)
    return <Loading label={t("common.loading")} />;
  const views: Record<string, JSX.Element> = {
    organization: <OrganizationSettings />,
    team: <TeamSettings />,
    billing: <BillingSettings />,
    integrations: <IntegrationSettings />,
    printers: <PrinterSettings />,
    account: <AccountSettings />,
  };
  const content = (
    <div
      key={`${impersonatedOrgId || currentUser.org_id}:${selected.key}`}
      className="settings-content"
    >
      {views[selected.key]}
    </div>
  );
  if (selected.permission && !can(selected.permission))
    return (
      <RequirePermission
        permission={selected.permission}
        fallbackPath={
          organizationSetupRequired ? "/organization/setup-info" : undefined
        }
      >
        {content}
      </RequirePermission>
    );
  return (
    <div className="settings-page">
      <header className="settings-heading">
        <h1>
          {organizationSetupRequired
            ? t("organization.createTitle")
            : t("title")}
        </h1>
      </header>
      {!organizationSetupRequired && (
        <nav aria-label={t("sectionsLabel")} className="settings-nav">
          {sections
            .filter((section) => !section.permission || can(section.permission))
            .map((section) => (
              <button
                key={section.key}
                type="button"
                aria-current={selected.key === section.key ? "page" : undefined}
                onClick={() => setParams({ section: section.key })}
              >
                {t(`sections.${section.key}`)}
              </button>
            ))}
        </nav>
      )}
      {content}
      {!sections.some((section) => section.key === requested) && (
        <Alert variant="info">{t("common.unknownSection")}</Alert>
      )}
    </div>
  );
}
